import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Box, Grid } from '@mui/material';
import SelectField from '../../../../../components/SelectField';
import DefaultConstants from '../../../../../data/Constants';
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum, RoomStatusData, RoomStatusEnum } from '../../../../../data/Enums';
import Switch from '../../../../../components/Switch';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import { IBlock, IFloor, IRoomType } from '../../../../../interfaces/IPartnerPms';
import { useUserContext } from '../../../../../contexts/UserContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';


export interface IRoomOverviewReportFilters {
    enterprise: IEnterprise | undefined;
    occupancyStatus: OccupancyStatusEnum[];
    governanceStatus: GovernanceStatusEnum[];
    roomStatus: RoomStatusEnum[];
    block: IBlock[];
    floor: IFloor[];
    roomType: IRoomType[];
    onlyUserRooms: boolean;
}


const initialFilters: IRoomOverviewReportFilters = {
    enterprise: undefined,
    occupancyStatus: [],
    governanceStatus: [],
    roomStatus: [],
    block: [],
    floor: [],
    roomType: [],
    onlyUserRooms: false,
};


const RoomOverviewFilters: React.FunctionComponent<IDefaultDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();

    const [filters, setFilters] = useState<IRoomOverviewReportFilters>({
        ...initialFilters,
        enterprise: enterprise
    });


    useEffect(() => {
        if (open) {
            setFilters({
                ...initialFilters, 
                enterprise: enterprise
            });
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        setFilters({
            ...initialFilters, 
            enterprise: filters.enterprise
        });
        // eslint-disable-next-line
    }, [filters.enterprise]);


    const handleChangeFilterValue = useCallback((name: string, value: any) => {
        setFilters({
            ...filters,
            [name]: value
        });
        // eslint-disable-next-line
    }, [filters]);


    const enterprisePartnerPms = useMemo(() => {
        return {
            blocks: filters.enterprise?.partnerPms.blocks ?? [],
            floors: filters.enterprise?.partnerPms.floors ?? [],
            roomTypes: filters.enterprise?.partnerPms.roomTypes ?? [],
            hasUserRooms: (filters.enterprise?.partnerPms.userRooms ?? []).length > 0
        }
    }, [filters.enterprise]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleOpenPreview(filters);
        // eslint-disable-next-line
    }, [filters]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('roomOverview')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={filters.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            handleChangeFilterValue(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('occupancyStatus')}
                        name='occupancyStatus'
                        value={filters.occupancyStatus.length > 0 ? filters.occupancyStatus : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const occupancyStatus = filters.occupancyStatus.length > 0 && values.includes(0) ? [] : Object.values(OccupancyStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            handleChangeFilterValue(name, occupancyStatus);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(Object.values(OccupancyStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('governanceStatus')}
                        name='governanceStatus'
                        value={filters.governanceStatus.length > 0 ? filters.governanceStatus : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const governanceStatus = filters.governanceStatus.length > 0 && values.includes(0) ? [] : Object.values(GovernanceStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            handleChangeFilterValue(name, governanceStatus);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('roomStatus')}
                        name='roomStatus'
                        value={filters.roomStatus.length > 0 ? filters.roomStatus : [0]}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomStatus = filters.roomStatus.length > 0 && values.includes(0) ? [] : Object.values(RoomStatusData).filter(s => values.includes(s.key)).map(s => s.key);
                            handleChangeFilterValue(name, roomStatus);
                        }}
                        required={false}
                        multiple={true}
                        data={[{ value: 0, text: translate('all') }].concat(Object.values(RoomStatusData).filter(s => s.partnerId).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('block')}
                        name='block'
                        value={filters.block.length > 0 ? filters.block.map(b => +b.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const block = filters.block.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.blocks.filter(b => values.includes(+b.id));
                            handleChangeFilterValue(name, block);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.blocks.map(b => {
                            return {
                                value: +b.id,
                                text: b.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('floor')}
                        name='floor'
                        value={filters.floor.length > 0 ? filters.floor.map(f => +f.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const floor = filters.floor.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.floors.filter(f => values.includes(+f.id));
                            handleChangeFilterValue(name, floor);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.floors.map(f => {
                            return {
                                value: +f.id,
                                text: f.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('roomType')}
                        name='roomType'
                        value={filters.roomType.length > 0 ? filters.roomType.map(r => +r.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const roomType = filters.roomType.length > 0 && values.includes(0) ? [] : enterprisePartnerPms.roomTypes.filter(r => values.includes(+r.id));
                            handleChangeFilterValue(name, roomType);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(enterprisePartnerPms.roomTypes.map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                {
                    enterprisePartnerPms.hasUserRooms && <Grid item xs={12}>
                        <Box padding={2}>
                            <Switch
                                label={translate('_showOnlyRoomsAssignedToMe')}
                                labelPlacement='end'
                                name='onlyUserRooms'
                                value={filters.onlyUserRooms ? 0 : 1}
                                checked={filters.onlyUserRooms}
                                onChange={e => handleChangeFilterValue(e.target.name, Boolean(+e.target.value))}
                            />
                        </Box>
                    </Grid>
                }
            </Grid>
        </Dialog>
    );
}

export default RoomOverviewFilters;